import React from 'react';
import {
  BookOpen,
  Calendar,
  Check,
  ChevronDown,
  Clock,
  CreditCard,
  Crown,
  DollarSign,
  Rocket,
  Edit3,
  Flag,
  Gift,
  Globe,
  Layers,
  Menu,
  RefreshCw,
  ShieldCheck,
  ShoppingBag,
  Sparkles,
  Star,
  TrendingUp,
  Users,
  Video,
  Youtube,
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

/**
 * Responsive Admin tab navigation.
 *
 * Replaces the old flat 22-tab strip that wrapped onto two cramped rows.
 * Now: 5 grouped dropdowns on tablet/desktop, single hamburger menu on
 * mobile, with the active tab always shown as a pill underneath.
 *
 * Each dropdown owns a logical cluster:
 *   • People       — Users, Founder, Featured, Downline, Approvals
 *   • Access       — Tiers, Tier Access Matrix, Custom Tiers
 *   • Money        — Sales, Promos, Pot, Commission, Revenue, Payouts
 *   • Content      — Media, YT, LMS Content, Scheduler
 *   • Tools        — Directory, Advertisers, Audit, Legal
 */

// Pure data — easy to extend. `key` matches the TabsContent value the
// parent `<Tabs>` switches on, so adding/removing tabs is one line each.
export const ADMIN_TAB_GROUPS = [
  {
    label: 'People',
    icon: Users,
    items: [
      { key: 'users', label: 'Users', icon: Users },
      { key: 'founder', label: 'Founder Membership', icon: Crown },
      { key: 'featured', label: 'Featured Members', icon: Star },
      { key: 'downline', label: 'Downline / MLM' },
      { key: 'approvals', label: 'Founder Approvals', badgeKey: 'pendingFoundersCount' },
    ],
  },
  {
    label: 'Access',
    icon: ShieldCheck,
    items: [
      { key: 'tiers', label: 'Tier Features', icon: Edit3 },
      { key: 'tier-access', label: 'Tier Access Matrix', icon: ShieldCheck },
      { key: 'custom-tiers', label: 'Custom Tiers', icon: Layers },
    ],
  },
  {
    label: 'Money',
    icon: DollarSign,
    items: [
      { key: 'sales', label: 'Sales Verification', icon: TrendingUp },
      { key: 'subscriptions', label: 'Subscriptions', icon: CreditCard, badgeKey: 'needsBackfillCount' },
      { key: 'promo', label: 'Promo Codes', icon: Gift },
      { key: 'pot', label: 'Millionaire Pot', icon: DollarSign },
      { key: 'commissions', label: 'Commission Settings' },
      { key: 'commission-dashboard', label: 'Revenue Dashboard' },
      { key: 'payouts', label: 'Stripe Payouts' },
      { key: 'stripe-reconcile', label: 'Stripe Sync', icon: RefreshCw },
    ],
  },
  {
    label: 'Content',
    icon: Video,
    items: [
      { key: 'videos', label: 'Media Library', icon: Video },
      { key: 'youtube', label: 'Video Approvals', icon: Youtube, badgeKey: 'pendingVideosCount' },
      { key: 'reports', label: 'Reports', icon: Flag, badgeKey: 'openReportsCount' },
      { key: 'content', label: 'LMS Content', icon: BookOpen },
      { key: 'onboarding', label: 'Onboarding', icon: Rocket },
      { key: 'scheduler', label: 'Scheduler', icon: Calendar },
    ],
  },
  {
    label: 'Tools',
    icon: Globe,
    items: [
      { key: 'ai-copilot', label: 'AI Copilot', icon: Sparkles },
      { key: 'verifications', label: 'Provider Verifications', icon: ShieldCheck },
      { key: 'edirectory', label: 'Directory Integration', icon: Globe },
      { key: 'advertisers', label: 'Advertiser Products', icon: ShoppingBag },
      { key: 'audit-log', label: 'Audit Log', icon: Clock },
      { key: 'compliance', label: 'Compliance', icon: ShieldCheck },
      { key: 'email-test', label: 'Email Delivery' },
      { key: 'legal', label: 'Legal Documents' },
    ],
  },
];

// Flat lookup so the active-pill renderer can find icon + label without
// re-walking the groups for every render.
const TAB_INDEX = ADMIN_TAB_GROUPS.flatMap((g) => g.items).reduce((acc, item) => {
  acc[item.key] = item;
  return acc;
}, {});

function ItemRow({ item, isActive, onSelect, badges }) {
  const Icon = item.icon;
  const badge = item.badgeKey ? badges?.[item.badgeKey] : 0;
  return (
    <DropdownMenuItem
      onSelect={() => onSelect(item.key)}
      className={`flex items-center gap-2 cursor-pointer ${isActive ? 'bg-primary/10 text-primary' : ''}`}
      data-testid={`admin-nav-item-${item.key}`}
    >
      {Icon ? <Icon className="w-4 h-4" /> : <span className="w-4 h-4" />}
      <span className="flex-1">{item.label}</span>
      {badge > 0 && (
        <span className="bg-red-500 text-white text-[10px] rounded-full px-1.5 py-0.5">
          {badge}
        </span>
      )}
      {isActive && <Check className="w-3.5 h-3.5 text-primary" />}
    </DropdownMenuItem>
  );
}

export default function AdminTabNav({ activeTab, setActiveTab, badges = {} }) {
  const active = TAB_INDEX[activeTab] || { label: activeTab, icon: null };
  const ActiveIcon = active.icon;

  return (
    <div className="space-y-2" data-testid="admin-tab-nav">
      {/* Desktop / tablet: 5 grouped dropdowns inline */}
      <div className="hidden md:flex flex-wrap items-center gap-2">
        {ADMIN_TAB_GROUPS.map((group) => {
          const GroupIcon = group.icon;
          const groupHasActive = group.items.some((it) => it.key === activeTab);
          // Aggregate badges in this group so the trigger can show a count
          // even when the active tab is elsewhere.
          const groupBadge = group.items.reduce(
            (sum, it) => sum + (it.badgeKey ? Number(badges?.[it.badgeKey]) || 0 : 0),
            0,
          );
          return (
            <DropdownMenu key={group.label}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant={groupHasActive ? 'default' : 'outline'}
                  size="sm"
                  className="gap-2"
                  data-testid={`admin-nav-group-${group.label.toLowerCase()}`}
                >
                  <GroupIcon className="w-4 h-4" />
                  {group.label}
                  {groupBadge > 0 && (
                    <span className="bg-red-500 text-white text-[10px] rounded-full px-1.5 py-0.5">
                      {groupBadge}
                    </span>
                  )}
                  <ChevronDown className="w-3.5 h-3.5 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-60">
                <DropdownMenuLabel>{group.label}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {group.items.map((item) => (
                  <ItemRow
                    key={item.key}
                    item={item}
                    isActive={activeTab === item.key}
                    onSelect={setActiveTab}
                    badges={badges}
                  />
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        })}
      </div>

      {/* Mobile: single hamburger that opens a flat list of all sections */}
      <div className="md:hidden">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="w-full justify-between"
              data-testid="admin-nav-mobile-trigger"
            >
              <span className="flex items-center gap-2">
                <Menu className="w-4 h-4" />
                {ActiveIcon ? <ActiveIcon className="w-4 h-4" /> : null}
                <span className="truncate">{active.label || 'Menu'}</span>
              </span>
              <ChevronDown className="w-4 h-4 opacity-60" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            className="w-[calc(100vw-2rem)] max-h-[70vh] overflow-y-auto"
          >
            {ADMIN_TAB_GROUPS.map((group, gi) => (
              <React.Fragment key={group.label}>
                {gi > 0 && <DropdownMenuSeparator />}
                <DropdownMenuLabel className="flex items-center gap-2">
                  <group.icon className="w-3.5 h-3.5" />
                  {group.label}
                </DropdownMenuLabel>
                {group.items.map((item) => (
                  <ItemRow
                    key={item.key}
                    item={item}
                    isActive={activeTab === item.key}
                    onSelect={setActiveTab}
                    badges={badges}
                  />
                ))}
              </React.Fragment>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Active-tab pill: gives the user a stable visual anchor below the
          menus on every viewport. */}
      <div
        className="flex items-center gap-2 text-sm text-muted-foreground"
        data-testid="admin-active-tab-pill"
      >
        <span className="opacity-60">Viewing:</span>
        <span className="inline-flex items-center gap-1.5 bg-primary/10 text-primary border border-primary/20 rounded-full px-2.5 py-0.5 font-medium">
          {ActiveIcon && <ActiveIcon className="w-3.5 h-3.5" />}
          {active.label}
        </span>
      </div>
    </div>
  );
}
